/**
 * Akten-Live (Admin / Backoffice)
 * --------------------------------
 * One-stop overview of every case from every Gutachter — the moment a case
 * is created (or even just touched) it shows up here in real time via the
 * /api/ws/cases WebSocket.  Toggle between Tabelle (data-rich) and Board
 * (kanban by status).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import AuthImg from "@/components/AuthImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CASE_STATUS_META, caseStatusCls, caseStatusLabel } from "@/lib/caseStatus";
import useCaseEvents from "@/lib/useCaseEvents";
import {
  ShieldCheck, Search, Loader2, ArrowRight, Wifi, WifiOff, Table2, LayoutGrid,
  Camera, Hash, RefreshCw, Eye, Hand, UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const KANBAN_COLUMNS = [
  "uploaded",
  "in_review",
  "ready_for_gutachter",
  "released",
  "sent_to_customer",
];

const STATUS_FILTERS = [
  { v: "all", l: "Alle Status" },
  { v: "draft", l: "Entwurf" },
  { v: "in_progress", l: "In Bearbeitung" },
  { v: "uploaded", l: "Hochgeladen" },
  { v: "in_review", l: "In Prüfung" },
  { v: "ready_for_gutachter", l: "Zur Freigabe" },
  { v: "released", l: "Freigegeben" },
  { v: "sent_to_customer", l: "An Kunden" },
  { v: "revision_requested", l: "Korrektur" },
  { v: "completed", l: "Fertiggestellt" },
];

export default function Backoffice() {
  const { accessToken } = useAuth();
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table"); // table | board
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [recentlyUpdated, setRecentlyUpdated] = useState(new Set());

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (ownerFilter !== "all") params.owner_id = ownerFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/cases", { params });
      setCases(data.cases || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, ownerFilter, search]);

  // Initial + when filters change
  useEffect(() => { fetchCases(); }, [fetchCases]);

  // Load gutachter list once
  useEffect(() => {
    api.get("/users/list-min")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => { /* not critical */ });
  }, []);

  // ---- WebSocket live updates ----
  const onEvent = useCallback((evt) => {
    if (!evt?.case_id) return;
    setRecentlyUpdated((prev) => {
      const next = new Set(prev);
      next.add(evt.case_id);
      return next;
    });
    // Auto-clear "new" highlight after 6s
    setTimeout(() => {
      setRecentlyUpdated((prev) => {
        const next = new Set(prev);
        next.delete(evt.case_id);
        return next;
      });
    }, 6000);

    if (evt.type === "case.deleted") {
      setCases((prev) => prev.filter((c) => c.id !== evt.case_id));
      return;
    }
    if (!evt.case) return;
    setCases((prev) => {
      const idx = prev.findIndex((c) => c.id === evt.case_id);
      // Keep owner_name we already have if backend didn't hydrate it
      const owner = prev[idx];
      const merged = { ...evt.case };
      if (owner?.owner_name && !merged.owner_name) merged.owner_name = owner.owner_name;
      if (owner?.owner_avatar_path && !merged.owner_avatar_path) merged.owner_avatar_path = owner.owner_avatar_path;
      const out = idx >= 0 ? [...prev] : [merged, ...prev];
      if (idx >= 0) out[idx] = merged;
      // Resort by updated_at desc so freshly-touched bubbles to top
      out.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
      return out;
    });
  }, []);
  const { connected: wsConnected } = useCaseEvents(onEvent);

  // ---- Akte übernehmen (Pull-Prinzip: fair, keine Doppelarbeit) ----
  const [claimingId, setClaimingId] = useState(null);
  const claimCase = useCallback(async (c) => {
    setClaimingId(c.id);
    try {
      const { data } = await api.post(`/cases/${c.id}/claim`);
      toast.success(`Akte ${c.case_number || ""} übernommen — viel Erfolg!`);
      setCases((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...data.case } : x)));
    } catch (e) {
      toast.error(apiError(e));
      fetchCases();
    } finally {
      setClaimingId(null);
    }
  }, [fetchCases]);

  // ---- KPIs ----
  const kpis = useMemo(() => {
    const base = { uploaded: 0, in_review: 0, ready_for_gutachter: 0, released: 0, today: 0, drafts_old: 0 };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const sevenDays = Date.now() - 7 * 86400000;
    for (const c of cases) {
      if (c.status in base) base[c.status] += 1;
      if (c.updated_at && new Date(c.updated_at) >= today) base.today += 1;
      if (c.status === "draft" && c.updated_at && new Date(c.updated_at).getTime() < sevenDays) base.drafts_old += 1;
    }
    return base;
  }, [cases]);

  // ---- Frontend search filter (server search is also applied) ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter((c) => {
      const hay = [
        c.title, c.case_number, c.customer?.name, c.customer?.firstname,
        c.vehicle?.license_plate, c.vehicle?.make, c.vehicle?.model, c.owner_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [cases, search]);

  // ---- Group by status for Board ----
  const groups = useMemo(() => {
    const out = {};
    for (const c of filtered) (out[c.status] ||= []).push(c);
    return out;
  }, [filtered]);

  // ---- Live online presence (admin/backoffice see all currently active colleagues) ----
  const [presence, setPresence] = useState({ online_count: 0, online_users: [] });
  useEffect(() => {
    let cancelled = false;
    const fetchPresence = async () => {
      try {
        const { data } = await api.get("/users/admin/presence");
        if (cancelled) return;
        const onlineUsers = (data.users || [])
          .filter((u) => u.is_online)
          .sort((a, b) => (b.last_seen_at || "").localeCompare(a.last_seen_at || ""));
        setPresence({ online_count: data.online_count || 0, online_users: onlineUsers });
      } catch (_e) { /* silent */ }
    };
    fetchPresence();
    const i = setInterval(fetchPresence, 20_000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  return (
    <Layout title="Akten-Live">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
          <ShieldCheck className="h-3 w-3 mr-1" /> Alle Akten · alle Gutachter
        </Badge>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1 border",
            wsConnected
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-zinc-50 text-zinc-500 border-zinc-200"
          )}
          data-testid="ws-status"
          title={wsConnected ? "Live verbunden" : "Live nicht verbunden — neu lade…"}
        >
          {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
          {wsConnected ? "Live" : "Offline"}
        </span>
        {/* Online colleagues indicator — at-a-glance who is currently working in the app */}
        {presence.online_count > 0 && (
          <div
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 pl-2 pr-3 py-1"
            data-testid="online-users-indicator"
            title={presence.online_users.map((u) => u.name || u.email).join(", ")}
          >
            <span className="relative inline-flex">
              <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-emerald-400 opacity-70"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            <div className="flex -space-x-2" data-testid="online-users-avatars">
              {presence.online_users.slice(0, 5).map((u) => (
                <div
                  key={u.id}
                  className="relative h-6 w-6 rounded-full ring-2 ring-white bg-zinc-100 overflow-hidden flex items-center justify-center text-[10px] font-semibold text-zinc-600"
                  title={`${u.name || u.email}${u.role ? ` · ${u.role}` : ""}`}
                >
                  {u.avatar_path ? (
                    <AuthImg path={u.avatar_path} className="h-full w-full object-cover" alt={u.name} />
                  ) : (
                    (u.name || u.email).slice(0, 2).toUpperCase()
                  )}
                </div>
              ))}
            </div>
            <span className="text-[11px] font-semibold text-emerald-800" data-testid="online-users-count">
              {presence.online_count} {presence.online_count === 1 ? "User aktiv" : "User aktiv"}
            </span>
          </div>
        )}
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={fetchCases} data-testid="refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Aktualisieren
        </Button>
        <div className="inline-flex rounded-xl border border-zinc-200 bg-white p-1" data-testid="view-toggle">
          <button
            onClick={() => setView("table")}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition",
              view === "table" ? "bg-emerald-600 text-white shadow-sm" : "text-zinc-600 hover:text-zinc-900"
            )}
            data-testid="view-table-btn"
          >
            <Table2 className="h-3.5 w-3.5" /> Tabelle
          </button>
          <button
            onClick={() => setView("board")}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition",
              view === "board" ? "bg-emerald-600 text-white shadow-sm" : "text-zinc-600 hover:text-zinc-900"
            )}
            data-testid="view-board-btn"
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Board
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="kpi-strip">
        <KPI label="Heute aktiv" value={kpis.today} tone="cyan" />
        <KPI label="Hochgeladen" value={kpis.uploaded} tone="cyan" />
        <KPI label="In Prüfung" value={kpis.in_review} tone="violet" />
        <KPI label="Zur Freigabe" value={kpis.ready_for_gutachter} tone="emerald" />
        <KPI label="Alte Entwürfe" value={kpis.drafts_old} tone="rose" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-5">
        <div className="relative flex-1 min-w-[180px] sm:max-w-[260px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Suche Aktennr., Kunde, Kennzeichen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="w-[200px]" data-testid="owner-filter"><SelectValue placeholder="Bearbeiter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Bearbeiter</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…
        </div>
      ) : view === "board" ? (
        <BoardView groups={groups} accessToken={accessToken} recentlyUpdated={recentlyUpdated} claimCase={claimCase} claimingId={claimingId} />
      ) : (
        <TableView cases={filtered} accessToken={accessToken} recentlyUpdated={recentlyUpdated} claimCase={claimCase} claimingId={claimingId} />
      )}
    </Layout>
  );
}

// ---------- KPI tile ----------
function KPI({ label, value, tone = "zinc" }) {
  const toneCls = {
    cyan: "text-cyan-700",
    emerald: "text-emerald-700",
    violet: "text-violet-700",
    rose: "text-rose-700",
    zinc: "text-zinc-700",
  }[tone] || "text-zinc-700";
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3.5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">{label}</div>
      <div className={cn("text-2xl font-display font-bold mt-1", toneCls)}>{value}</div>
    </div>
  );
}

// ---------- Avatar pill ----------
function OwnerPill({ owner_name, owner_avatar_path, accessToken }) {
  const initials = (owner_name || "")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase()).join("") || "?";
  return (
    <div className="inline-flex items-center gap-2 min-w-0">
      <div className="h-7 w-7 rounded-full overflow-hidden bg-gradient-to-br from-emerald-500 to-cyan-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
        {owner_avatar_path
          ? <AuthImg path={owner_avatar_path} token={accessToken} alt={owner_name} className="w-full h-full object-cover" />
          : <span>{initials}</span>}
      </div>
      <span className="text-sm truncate text-zinc-800">{owner_name || "—"}</span>
    </div>
  );
}

// ---------- Geprüft-von / Übernehmen-Zelle ----------
function AssigneeCell({ c, claimCase, claimingId }) {
  if (c.assigned_to_name) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-1 max-w-full"
            data-testid={`assignee-${c.case_number}`}>
        <UserCheck className="h-3 w-3 shrink-0" />
        <span className="truncate">{c.assigned_to_name}</span>
      </span>
    );
  }
  if (c.status === "uploaded") {
    return (
      <Button
        size="sm"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); claimCase(c); }}
        disabled={claimingId === c.id}
        className="h-7 px-2.5 bg-violet-600 hover:bg-violet-700 text-white text-xs"
        data-testid={`claim-btn-${c.case_number}`}
      >
        {claimingId === c.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Hand className="h-3 w-3 mr-1" />}
        Übernehmen
      </Button>
    );
  }
  return <span className="text-xs text-zinc-400">—</span>;
}

// ---------- TABLE VIEW ----------
function TableView({ cases, accessToken, recentlyUpdated, claimCase, claimingId }) {
  if (!cases.length) {
    return <div className="text-center py-16 text-zinc-500" data-testid="empty">Keine Akten gefunden</div>;
  }
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="cases-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
            <tr className="border-b border-zinc-200">
              <th className="text-left px-4 py-3 w-[110px]">Aktennr.</th>
              <th className="text-left px-4 py-3">Kunde</th>
              <th className="text-left px-4 py-3 w-[120px]">Kennz.</th>
              <th className="text-left px-4 py-3 w-[160px]">Bearbeiter</th>
              <th className="text-left px-4 py-3 w-[150px]">Status</th>
              <th className="text-left px-4 py-3 w-[100px]">Fortschritt</th>
              <th className="text-left px-4 py-3 w-[110px]">Letzte Änderung</th>
              <th className="text-right px-4 py-3 w-[100px]">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {cases.map((c) => {
              const isNew = recentlyUpdated.has(c.id);
              const completeness = computeCompleteness(c);
              return (
                <tr
                  key={c.id}
                  className={cn(
                    "border-b border-zinc-100 hover:bg-zinc-50/70 transition",
                    isNew && "bg-emerald-50/40"
                  )}
                  data-testid={`row-${c.case_number}`}
                >
                  <td className="px-4 py-3 font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                      {isNew && <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" data-testid="new-dot" />}
                      <Hash className="h-3 w-3 text-zinc-400" />
                      {c.case_number || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 truncate max-w-[200px]">
                    <div className="font-medium text-zinc-900 truncate">{c.customer?.name || c.title || "—"}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {[c.vehicle?.make, c.vehicle?.model].filter(Boolean).join(" ") || "—"}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{c.vehicle?.license_plate || "—"}</td>
                  <td className="px-4 py-3"><OwnerPill {...c} accessToken={accessToken} /></td>
                  <td className="px-4 py-3">
                    <AssigneeCell c={c} claimCase={claimCase} claimingId={claimingId} />
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={caseStatusCls(c.status)}>
                      {caseStatusLabel(c.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-xs text-zinc-600">
                      <Camera className="h-3 w-3" /> {c.photos?.length || 0}
                      <span className="text-zinc-400">·</span>
                      <span>{completeness}/7</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">{fmtDate(c.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/cases/${c.id}`}
                      className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 text-xs font-semibold"
                      data-testid={`open-${c.case_number}`}
                    >
                      <Eye className="h-3.5 w-3.5" /> Öffnen
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- BOARD VIEW ----------
function BoardView({ groups, accessToken, recentlyUpdated, claimCase, claimingId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="cases-board">
      {KANBAN_COLUMNS.map((col) => {
        const items = groups[col] || [];
        const meta = CASE_STATUS_META[col];
        return (
          <div key={col} className="rounded-2xl bg-zinc-50/70 border border-zinc-200 p-3 min-h-[400px] flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
              <span className="text-xs text-zinc-500 font-mono">{items.length}</span>
            </div>
            <div className="space-y-2 flex-1 overflow-y-auto">
              {items.map((c) => (
                <BoardCard key={c.id} c={c} accessToken={accessToken} isNew={recentlyUpdated.has(c.id)} claimCase={claimCase} claimingId={claimingId} />
              ))}
              {!items.length && <div className="text-xs text-zinc-400 text-center py-6">— leer —</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BoardCard({ c, accessToken, isNew, claimCase, claimingId }) {
  return (
    <Link
      to={`/cases/${c.id}`}
      className={cn(
        "block rounded-xl bg-white border p-3 hover:shadow-md hover:border-zinc-300 transition relative",
        isNew ? "border-emerald-300 ring-2 ring-emerald-100" : "border-zinc-200"
      )}
      data-testid={`board-card-${c.case_number}`}
    >
      {isNew && <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />}
      <div className="text-[10px] text-zinc-400 font-mono">{c.case_number}</div>
      <div className="font-semibold text-sm mt-0.5 truncate">{c.customer?.name || c.title}</div>
      <div className="text-[11px] text-zinc-500 truncate">
        {[c.vehicle?.make, c.vehicle?.model, c.vehicle?.license_plate].filter(Boolean).join(" · ")}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <OwnerPill {...c} accessToken={accessToken} />
        <AssigneeCell c={c} claimCase={claimCase} claimingId={claimingId} />
      </div>
    </Link>
  );
}

function computeCompleteness(c) {
  let n = 0;
  if (c.customer?.name && c.customer?.address) n++;
  if (c.insurance?.insurer_name && c.insurance?.claim_number) n++;
  if (c.vehicle?.vin || c.vehicle?.license_plate) n++;
  if ((c.photos?.length || 0) > 0) n++;
  if (c.signature) n++;
  if (Object.keys(c.pre_damages || {}).length > 0) n++;
  if (c.damage_description) n++;
  return n;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return "gerade eben";
    if (diffMs < 3600000) return `vor ${Math.floor(diffMs / 60000)} Min.`;
    if (diffMs < 86400000) return `vor ${Math.floor(diffMs / 3600000)} Std.`;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  } catch (_) { return iso; }
}
